import i18n from "intl/i18n"
import dayjs, { Dayjs } from "utils/dayjs"

import { ApiLinkEntity, ApiLinkRel, ArticleType } from "config/constants"
import { Guid, IApiLink, ITranslations } from "@/types"
import { formatNumber, getAssetDisposalReasonLabel } from "utils/utils"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"
import { ArticleClass } from "./ArticleClassInfo"
import { AssetDisposalReason, AssetOrigin, AssetOwnershipType, AssetStatus, AssetAvailability, IAssetBuyBack } from "./Asset"
import { AssetStoredStatus } from "./AssetDisplayStatus"
import Address, { IAddressDto } from "./Address"
import Unit from "./Unit"

export enum AssetAvailabilityIndicatorState {
    available = "available",           // green - backend says available
    requestPlanned = "requestPlanned", // yellow - future non-sale request
    notAvailable = "notAvailable",     // red - in non-sale request now
    salesCrPlanned = "salesCrPlanned", // blue - sales request (current or future)
    inactive = "inactive",             // grey - inactive / future availability / reserved
    borrowExpired = "borrowExpired"    // grey - borrowed asset overdue
}

export interface IAssetAvailabilityIndicatorInfo {
    state: AssetAvailabilityIndicatorState
    color: "green" | "yellow" | "red" | "blue" | "grey"
    label: string
    sublabel?: string
    clickable: boolean
    crNumber?: string
    crId?: Guid
    disposalReason?: string
}

export interface IAssetArticleDto {
    articleId: Guid
    assetId: Guid
    lmid: string
    productLineDescription?: string | null
    productFamilyDescription?: string | null
    productModelDescription?: string | null
    articleType: ArticleType
    articleClass?: ArticleClass | null
    inventoryNumber?: string | null
    serialNumber?: string | null
    serialNumberMachine?: string | null
    manufacturerCode?: string | null
    isOperational: boolean
    isLabeled: boolean
    assetCollectionId?: Guid | null
    constructionYear: number
    operationHours?: number | null
    availabilityDate: string
    availability?: string | null
    plannedUntilOrUnavailableFromDate?: string | null
    availabilityReservationNumber?: string | null
    availabilityReservationId?: Guid | null
    expectedReturnDate?: string | null
    lastScanDate?: string | null
    features?: ITranslations | null
    hasFreightUnits: boolean
    articleDescription?: string | null
    articleDescriptionTranslations?: ITranslations | null
    altArticleDescription?: string | null
    articleNo?: string | null
    drawingNo?: string | null
    status: AssetStatus
    internalOrderNumber?: string | null
    supplierOrderNo?: string | null
    profitCenterId?: Guid | null
    holderName?: string | null
    latitude?: number | null
    longitude?: number | null
    homebaseId?: Guid | null
    warehouseId?: Guid | null
    customerName?: string | null
    weight?: number | null
    width?: number | null
    height?: number | null
    length?: number | null
    inch?: number | null
    measurementSystemId?: Guid | null
    quickCouplingSystem?: string | null
    conntectionType?: string | null
    cuttingEdge?: boolean | null
    teeth?: boolean | null
    heavyDuty?: boolean | null
    colorId?: Guid | null
    labels: string[]
    imageUri?: string | null
    thumbnailUri?: string | null
    ownershipType: AssetOwnershipType
    disposal?: {
        reason: AssetDisposalReason
        date: string
        description?: string | null
    } | null
    relevantForAssetStatistic?: boolean
    reportAsset?: boolean
    relatedRequest?: Guid | null
    relatedRequestNumber?: string | null
    condition?: number | null
    initialCondition: boolean
    assetOrigin?: AssetOrigin | null
    emissionLevel?: string | null
    assetFinancialAndListPrice?: {
        referenceNumberSale?: string | null
        initialReferenceNumberSale?: string | null
        referenceNumberRental?: string | null
        initialReferenceNumberRental?: string | null
        referenceValue?: IAmountWithCurrencyDto | null
        referenceValueCorrection?: number | null
        monthlyRentalCost?: IAmountWithCurrencyDto | null
        productGroup?: string | null
        assetCategory?: string | null
        assetType?: string | null
        listPrice?: number | null
        listPriceDate?: string | null
        discount?: number | null
        discountAmount?: number | null
        salesSupport?: number | null
        salesPrice?: number | null
        salesPriceAdjustedCondition?: number | null
    } | null
    buyBack?: IAssetBuyBack
    stateOfStorage: AssetStoredStatus
    requestId?: Guid | null
    requestNumber?: string | null
    eventAddress?: IAddressDto | null
    highLevelArticleNumber?: string | null
    lowLevelArticleNumbers?: string[] | null
    links?: IApiLink[]
    rentToBuy?: number | boolean | null
    version?: number | null
    isCurrentVersion?: boolean | null
}

export default class AssetArticle {
    articleId: Guid
    assetId: Guid
    lmid: string
    productLineDescription?: string
    productFamilyDescription?: string
    productModelDescription?: string
    articleType: ArticleType
    articleClass?: ArticleClass
    inventoryNumber?: string
    serialNumber?: string
    serialNumberMachine?: string
    manufacturerCode?: string
    isOperational: boolean
    isLabeled: boolean
    assetCollectionId?: Guid
    constructionYear: number
    operationHours?: number
    availabilityDate: Dayjs
    availability?: string
    plannedUntilOrUnavailableFromDate?: Dayjs
    availabilityReservationNumber?: string
    availabilityReservationId?: Guid
    lastScanDate?: Dayjs
    expectedReturnDate?: Dayjs
    features?: ITranslations
    hasFreightUnits: boolean
    articleDescription?: string
    articleDescriptionTranslations?: ITranslations
    altArticleDescription?: string
    articleNo?: string
    drawingNo?: string
    status: AssetStatus
    internalOrderNumber?: string
    supplierOrderNo?: string
    profitCenterId?: Guid
    holderName?: string
    latitude?: number
    longitude?: number
    homebaseId?: Guid
    warehouseId?: Guid
    customerName?: string
    weight?: number
    width?: number
    height?: number
    length?: number
    inch?: number
    measurementSystemId?: Guid
    quickCouplingSystem?: string
    conntectionType?: string
    cuttingEdge?: boolean
    teeth?: boolean
    heavyDuty?: boolean
    colorId?: Guid
    labels: string[]
    imageUri?: string
    thumbnailUri?: string
    ownershipType: AssetOwnershipType
    disposal?: {
        reason: AssetDisposalReason
        date: Dayjs
        description?: string
    }
    relevantForAssetStatistic?: boolean
    reportAsset?: boolean
    relatedRequest?: Guid
    relatedRequestNumber?: string
    condition?: number
    initialCondition: boolean
    assetOrigin?: AssetOrigin
    emissionLevel?: string
    referenceNumberSale?: string
    initialReferenceNumberSale?: string
    referenceNumberRental?: string
    initialReferenceNumberRental?: string
    referenceValue?: AmountWithCurrency
    referenceValueCorrection?: number
    monthlyRentalCost?: AmountWithCurrency
    productGroup?: string
    assetCategory?: string
    assetType?: string
    listPrice?: number
    listPriceDate?: Dayjs
    discount?: number
    discountAmount?: number
    salesSupport?: number
    salesPrice?: number
    salesPriceAdjustedCondition?: number
    buyBack?: IAssetBuyBack
    stateOfStorage: AssetStoredStatus
    requestId?: Guid
    requestNumber?: string
    eventAddress?: Address
    highLevelArticleNumber?: string | null
    lowLevelArticleNumbers: string[]
    linkUpdate?: IApiLink
    linkReactivate?: IApiLink
    rentToBuy?: number | boolean
    version?: number
    isCurrentVersion: boolean

    constructor(dto: IAssetArticleDto) {
        this.articleId = dto.articleId
        this.assetId = dto.assetId
        this.lmid = dto.lmid
        this.productLineDescription = dto.productLineDescription ?? undefined
        this.productFamilyDescription = dto.productFamilyDescription ?? undefined
        this.productModelDescription = dto.productModelDescription ?? undefined
        this.articleType = dto.articleType
        this.articleClass = dto.articleClass ?? undefined
        this.inventoryNumber = dto.inventoryNumber ?? undefined
        this.serialNumber = dto.serialNumber ?? undefined
        this.serialNumberMachine = dto.serialNumberMachine ?? undefined
        this.manufacturerCode = dto.manufacturerCode ?? undefined
        this.isOperational = dto.isOperational
        this.isLabeled = dto.isLabeled
        this.assetCollectionId = dto.assetCollectionId ?? undefined
        this.constructionYear = dto.constructionYear
        this.operationHours = typeof dto.operationHours === "number" ? dto.operationHours : undefined
        this.availabilityDate = dayjs.utc(dto.availabilityDate)
        this.availability = dto.availability || undefined
        this.plannedUntilOrUnavailableFromDate = dto.plannedUntilOrUnavailableFromDate ? dayjs.utc(dto.plannedUntilOrUnavailableFromDate) : undefined
        this.availabilityReservationNumber = dto.availabilityReservationNumber || undefined
        this.availabilityReservationId = dto.availabilityReservationId || undefined
        this.lastScanDate = dto.lastScanDate ? dayjs(dto.lastScanDate) : undefined
        this.expectedReturnDate = dto.expectedReturnDate ? dayjs.utc(dto.expectedReturnDate) : undefined
        this.features = dto.features ?? undefined
        this.hasFreightUnits = dto.hasFreightUnits
        this.articleDescription = dto.articleDescription || undefined
        this.articleDescriptionTranslations = dto.articleDescriptionTranslations || undefined
        this.altArticleDescription = dto.altArticleDescription || undefined
        this.articleNo = dto.articleNo || undefined
        this.drawingNo = dto.drawingNo || undefined
        this.status = dto.status
        this.internalOrderNumber = dto.internalOrderNumber || undefined
        this.supplierOrderNo = dto.supplierOrderNo || undefined
        this.profitCenterId = dto.profitCenterId || undefined
        this.holderName = dto.holderName || undefined
        this.latitude = typeof dto.latitude === "number" ? dto.latitude : undefined
        this.longitude = typeof dto.longitude === "number" ? dto.longitude : undefined
        this.homebaseId = dto.homebaseId || undefined
        this.warehouseId = dto.warehouseId || undefined
        this.customerName = dto.customerName ?? undefined
        this.weight = typeof dto.weight === "number" ? dto.weight : undefined
        this.width = typeof dto.width === "number" ? dto.width : undefined
        this.height = typeof dto.height === "number" ? dto.height : undefined
        this.length = typeof dto.length === "number" ? dto.length : undefined
        this.inch = typeof dto.inch === "number" ? dto.inch : undefined
        this.measurementSystemId = dto.measurementSystemId ?? undefined
        this.quickCouplingSystem = dto.quickCouplingSystem ?? undefined
        this.conntectionType = dto.conntectionType ?? undefined
        this.cuttingEdge = dto.cuttingEdge ?? undefined
        this.teeth = dto.teeth ?? undefined
        this.heavyDuty = dto.heavyDuty ?? undefined
        this.colorId = dto.colorId || undefined
        this.labels = dto.labels || []
        this.imageUri = dto.imageUri || undefined
        this.thumbnailUri = dto.thumbnailUri || undefined
        this.ownershipType = dto.ownershipType
        this.disposal = dto.disposal ? {
            reason: dto.disposal.reason,
            date: dayjs.utc(dto.disposal.date),
            description: dto.disposal.description || undefined,
        } : undefined
        this.relevantForAssetStatistic = dto.relevantForAssetStatistic
        this.reportAsset = dto.reportAsset
        this.relatedRequest = dto.relatedRequest ?? undefined
        this.relatedRequestNumber = dto.relatedRequestNumber ?? undefined
        this.condition = dto.condition ?? undefined
        this.initialCondition = dto.initialCondition
        this.assetOrigin = dto.assetOrigin ?? undefined
        this.emissionLevel = dto.emissionLevel ?? undefined
        this.referenceNumberSale = dto.assetFinancialAndListPrice?.referenceNumberSale ?? undefined
        this.initialReferenceNumberSale = dto.assetFinancialAndListPrice?.initialReferenceNumberSale ?? undefined
        this.referenceNumberRental = dto.assetFinancialAndListPrice?.referenceNumberRental ?? undefined
        this.initialReferenceNumberRental = dto.assetFinancialAndListPrice?.initialReferenceNumberRental ?? undefined
        this.referenceValue = dto.assetFinancialAndListPrice?.referenceValue ? new AmountWithCurrency(dto.assetFinancialAndListPrice?.referenceValue) : undefined
        this.referenceValueCorrection = dto.assetFinancialAndListPrice?.referenceValueCorrection ?? undefined
        this.monthlyRentalCost = dto.assetFinancialAndListPrice?.monthlyRentalCost ? new AmountWithCurrency(dto.assetFinancialAndListPrice?.monthlyRentalCost) : undefined
        this.productGroup = dto.assetFinancialAndListPrice?.productGroup ?? undefined
        this.assetCategory = dto.assetFinancialAndListPrice?.assetCategory ?? undefined
        this.assetType = dto.assetFinancialAndListPrice?.assetType ?? undefined
        this.listPrice = typeof dto.assetFinancialAndListPrice?.listPrice === "number" ? dto.assetFinancialAndListPrice?.listPrice : undefined
        this.listPriceDate = dto.assetFinancialAndListPrice?.listPriceDate ? dayjs(dto.assetFinancialAndListPrice?.listPriceDate, "YYYY-MM-DD") : undefined
        this.discount = typeof dto.assetFinancialAndListPrice?.discount === "number" ? dto.assetFinancialAndListPrice?.discount : undefined
        this.discountAmount = typeof dto.assetFinancialAndListPrice?.discountAmount === "number" ? dto.assetFinancialAndListPrice?.discountAmount : undefined
        this.salesSupport = typeof dto.assetFinancialAndListPrice?.salesSupport === "number" ? dto.assetFinancialAndListPrice?.salesSupport : undefined
        this.salesPrice = typeof dto.assetFinancialAndListPrice?.salesPrice === "number" ? dto.assetFinancialAndListPrice?.salesPrice : undefined
        this.salesPriceAdjustedCondition = typeof dto.assetFinancialAndListPrice?.salesPriceAdjustedCondition === "number" ? dto.assetFinancialAndListPrice?.salesPriceAdjustedCondition : undefined
        this.buyBack = dto.buyBack
        this.stateOfStorage = dto.stateOfStorage
        this.requestId = dto.requestId ?? undefined
        this.requestNumber = dto.requestNumber ?? undefined
        this.eventAddress = dto.eventAddress ? new Address(dto.eventAddress) : undefined
        this.highLevelArticleNumber = dto.highLevelArticleNumber ?? undefined
        this.lowLevelArticleNumbers = dto.lowLevelArticleNumbers ?? []
        this.linkUpdate = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.asset && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkReactivate = dto.links?.find(lnk => lnk.entity === ApiLinkEntity.assetReactivation && lnk.rel === ApiLinkRel.new)
        this.rentToBuy = typeof dto.rentToBuy === 'number' || typeof dto.rentToBuy === 'boolean' ? dto.rentToBuy : undefined
        this.version = typeof dto.version === 'number' ? dto.version : undefined
        this.isCurrentVersion = dto.isCurrentVersion ?? true
    }

    printDimensions(unit: Unit | null): string {
        const unitLabel = unit?.printShort() || ""
        const parts: string[] = []
        if (typeof this.length === "number" && this.length > 0) parts.push(`${i18n.t('common.dimensionShort.length')} ${formatNumber(this.length)} ${unitLabel}`)
        if (typeof this.width === "number" && this.width > 0) parts.push(`${i18n.t('common.dimensionShort.width')} ${formatNumber(this.width)} ${unitLabel}`)
        if (typeof this.height === "number" && this.height > 0) parts.push(`${i18n.t('common.dimensionShort.height')} ${formatNumber(this.height)} ${unitLabel}`)
        if (!unitLabel) return `${parts.join(" / ")}${typeof this.inch === "number" && this.inch > 0 ? ' R ' + this.inch : ''}`
        return parts.join(" × ")
    }

    get linkedToAssetCollection(): boolean { return !!this.assetCollectionId }
    get hasFeaturesOrFreightUnits(): boolean { return this.hasFreightUnits || (!!this.features && Object.values(this.features).some(t => !!t)) }
    get isPreviousVersion(): boolean { return !this.isCurrentVersion }
    get isInactive(): boolean { return this.status === AssetStatus.inactive }
    get shouldBeGreyedOut(): boolean { return this.isPreviousVersion || this.isInactive }
    get hasVersionInfo(): boolean { return typeof this.version === 'number' }

    get availabilityIndicatorInfo(): IAssetAvailabilityIndicatorInfo {
        const now = dayjs();
        // Overdue borrowed asset (show as grey instead of red to indicate passive overdue state)
        if (this.ownershipType === AssetOwnershipType.borrowed && this.expectedReturnDate && this.status !== AssetStatus.inactive && this.expectedReturnDate.isBefore(now)) {
            return {
                state: AssetAvailabilityIndicatorState.borrowExpired,
                color: "grey",
                label: `${i18n.t('availabilityUi.label.toBeReturnedOn')}` + `\n${this.expectedReturnDate.format(i18n.t('config.dateLongFormat'))}`,
                clickable: false
            }
        }

        let base: IAssetAvailabilityIndicatorInfo
        switch (this.availability) {
            case AssetAvailability.inactive:
                base = { state: AssetAvailabilityIndicatorState.inactive, color: "grey", label: this.disposal?.reason ? getAssetDisposalReasonLabel(this.disposal.reason) : "", clickable: false }
                break
            case AssetAvailability.notAvailable: {
                const dateStr = this.availabilityDate?.format(i18n.t('config.dateLongFormat'))
                base = { state: AssetAvailabilityIndicatorState.notAvailable, color: "grey", label: `${i18n.t('availabilityUi.label.availableFrom')}${dateStr ? `\n${dateStr}` : ''}`, clickable: false }
                break
            }
            case AssetAvailability.available:
                base = { state: AssetAvailabilityIndicatorState.available, color: "green", label: i18n.t('availabilityUi.label.available'), clickable: false }
                break
            case AssetAvailability.planned: {
                const cr = this.availabilityReservationNumber
                const start = this.plannedUntilOrUnavailableFromDate?.format(i18n.t('config.dateLongFormat'))
                let label: string
                if (cr && start) label = `${cr}\n${i18n.t('availabilityUi.label.starts')} ${start}`
                else if (cr) label = cr
                else if (start) label = `${i18n.t('availabilityUi.label.starts')} ${start}`
                else label = i18n.t('availabilityUi.label.reserved')
                base = { state: AssetAvailabilityIndicatorState.requestPlanned, color: "yellow", label, clickable: !!this.availabilityReservationId, crNumber: this.availabilityReservationNumber, crId: this.availabilityReservationId }
                break
            }
            case AssetAvailability.salePlanned: {
                const cr = this.availabilityReservationNumber
                const start = this.plannedUntilOrUnavailableFromDate?.format(i18n.t('config.dateLongFormat'))
                // Always show start date if provided (even if in the past)
                let label: string
                if (cr && start) {
                    label = `${cr}\n${i18n.t('availabilityUi.label.starts')} ${start}`
                } else if (cr && !start) {
                    label = cr
                } else if (!cr && start) {
                    label = `${i18n.t('availabilityUi.label.starts')} ${start}`
                } else {
                    label = i18n.t('availabilityUi.label.saleReserved')
                }
                base = { state: AssetAvailabilityIndicatorState.salesCrPlanned, color: "blue", label, clickable: !!this.availabilityReservationId, crNumber: this.availabilityReservationNumber, crId: this.availabilityReservationId }
                break
            }
            case AssetAvailability.inUse: {
                const cr = this.availabilityReservationNumber || this.requestNumber
                const end = this.plannedUntilOrUnavailableFromDate?.format(i18n.t('config.dateLongFormat'))
                let label: string
                if (cr) {
                    if (end) label = `${cr}\n${i18n.t('availabilityUi.label.ends')} ${end}`
                    else label = `${cr}\n${i18n.t('availabilityUi.label.inUse')}`
                } else label = i18n.t('availabilityUi.label.inUse')
                base = { state: AssetAvailabilityIndicatorState.notAvailable, color: "red", label, clickable: !!(this.availabilityReservationId || this.requestId), crNumber: cr, crId: this.availabilityReservationId || this.requestId }
                break
            }
            default:
                base = { state: AssetAvailabilityIndicatorState.available, color: "green", label: i18n.t('availabilityUi.label.available'), clickable: false }
                break
        }

        // Removed future borrowed return override: keep assets with a future expectedReturnDate green if available

        return base
    }
}
